import { useState } from "react";
import { cn } from "@/lib/utils";

// Helper to optimize Supabase URLs
// When a custom objectPosition is set (not center), we skip the server-side height
// crop so the browser can apply the correct focal point via CSS object-position.
export const getSupabaseOptimizedUrl = (url: string, width?: number, height?: number, quality = 90, objectPosition?: string) => {
    if (!url) return url;

    // Check if it's a Supabase Storage URL
    if (url.includes("supabase.co/storage/v1/object/public")) {
        const params = new URLSearchParams();
        const hasCustomPosition = objectPosition && objectPosition !== 'center' && objectPosition !== '50% 50%';

        if (width) params.append("width", width.toString());

        // Option A: When a custom position is set, skip server-side height crop
        // so the full image is delivered and the browser handles cropping via
        // object-fit: cover + object-position. For center (default), keep the
        // server crop for better performance.
        if (height && !hasCustomPosition) {
            params.append("height", height.toString());
            params.append("resize", "cover");
        }

        params.append("quality", quality.toString());
        params.append("format", "webp");

        // Switch from /object/public to /render/image/public to enable on-the-fly transformations
        const renderUrl = url.replace("/object/public", "/render/image/public");

        return `${renderUrl}?${params.toString()}`;
    }

    return url;
};

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    quality?: number;
    className?: string;
    /** CSS object-position value, e.g. "center", "50% 30%", "top" */
    objectPosition?: string;
}

export function OptimizedImage({
    src,
    alt,
    width,
    height,
    quality = 90,
    className,
    skipAnimation = false,
    objectPosition,
    ...props
}: OptimizedImageProps & { skipAnimation?: boolean }) {
    const [error, setError] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const optimizedUrl = getSupabaseOptimizedUrl(src, width, height, quality, objectPosition);

    return (
        <img
            src={error ? src : optimizedUrl}
            alt={alt}
            width={width}
            height={height}
            loading={props.loading || "lazy"}
            decoding="async"
            className={cn(
                "backface-hidden", // Force a hardware layer
                !skipAnimation && "transition-opacity duration-500 ease-out",
                !skipAnimation ? (isLoaded ? "opacity-100" : "opacity-0") : "",
                className
            )}
            onLoad={() => {
                if (!skipAnimation) setIsLoaded(true);
            }}
            onError={(e) => {
                setError(true);
                if (!skipAnimation) setIsLoaded(true);
                // Fallback to original src if transformation fails
                e.currentTarget.src = src;
            }}
            style={{
                ...props.style,
                transform: 'translateZ(0)', // Force GPU layer for Safari
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                ...(objectPosition ? { objectPosition } : {}),
            }}
            {...props}
        />
    );
}
